import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { fmtDate } from "@/lib/dates";

interface Meal {
  id: number;
  time: string | null;
  meal_type: string;
  description: string;
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number | null;
  source: string;
  photo_path: string | null;
}

interface DayDetail {
  date: string;
  meal_count: number;
  intake_kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  training_burn_kcal: number;
  target_kcal: number;
  target_protein_g: number;
  target_carbs_g: number;
  target_fat_g: number;
  deviation_kcal: number;
  adequacy: {
    protein_per_kg: number;
    carbs_per_kg: number;
    fat_pct_kcal: number;
    is_training_day: boolean;
    deviation_kcal: number;
    flags: Array<{
      macro: string;
      severity: string;
      issue: string;
      suggestion?: string;
    }>;
  };
  meals: Meal[];
}

const MEAL_LABEL: Record<string, string> = {
  breakfast: "meal_type.breakfast",
  lunch: "meal_type.lunch",
  dinner: "meal_type.dinner",
  snack: "meal_type.snack",
};

const SEV_STYLE: Record<string, string> = {
  high: "border-coral/50 bg-coral/10",
  medium: "border-amber/40 bg-amber/10",
  low: "border-cream-border bg-cream",
};

export default function NutritionDayDetail() {
  const { t } = useTranslation();
  const { date } = useParams<{ date: string }>();

  const q = useQuery({
    queryKey: ["nutrition-day", date],
    queryFn: async () => {
      const { data } = await api.get<DayDetail>(`/nutrition/day/${date}`);
      return data;
    },
    enabled: !!date,
  });

  if (q.isLoading) {
    return <div className="py-6 text-center text-taupe">{t("meal_detail.loading")}</div>;
  }
  if (!q.data) {
    return <div className="py-6 text-center text-coral">{t("nutrition_archive.load_failed")}</div>;
  }
  const d = q.data;
  const over = d.deviation_kcal > d.target_kcal * 0.1;
  const under = d.deviation_kcal < -d.target_kcal * 0.1;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <Link
        to="/nutrition/archive"
        className="inline-flex items-center gap-1 text-[13px] text-taupe hover:text-navy-deep"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
        {t("nutrition_day_detail.back_to_archive")}
      </Link>

      <header>
        <SectionLabel>{t("nutrition_day_detail.section_label")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug capitalize">
          {fmtDate(d.date, "EEEE, d MMMM yyyy")}
        </h1>
        <div className="mt-1 text-[13px] text-taupe">
          {t("nutrition_day_detail.n_meals", { n: d.meal_count })}
          {d.training_burn_kcal > 0
            ? ` · ${t("nutrition_archive.training_kcal_inline", { n: d.training_burn_kcal })}`
            : ` · ${t("nutrition_day_detail.rest_day")}`}
          {` · ${t("nutrition_day_detail.target_kcal_inline")} `}
          {d.target_kcal} {t("nutrition_archive.kcal")}
        </div>
      </header>

      <Card>
        <SectionLabel>{t("nutrition_day_detail.calories")}</SectionLabel>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-taupe">{t("nutrition_day_detail.eaten")}</div>
            <div className="metric-number text-[28px] text-navy-deep">
              {d.intake_kcal}
            </div>
            <div className="text-[11px] text-taupe">{t("nutrition_archive.kcal")}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-taupe">{t("nutrition_day_detail.target")}</div>
            <div className="metric-number text-[28px] text-navy-deep">
              {d.target_kcal}
            </div>
            <div className="text-[11px] text-taupe">ккал</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-taupe">{t("nutrition_day_detail.diff")}</div>
            <div
              className={`metric-number text-[28px] ${
                over ? "text-coral" : under ? "text-amber-dark" : "text-navy-deep"
              }`}
            >
              {d.deviation_kcal > 0 ? "+" : ""}
              {d.deviation_kcal}
            </div>
            <div className="text-[11px] text-taupe">
              {over ? t("nutrition_day_detail.over") : under ? t("nutrition_day_detail.under") : t("nutrition_day_detail.in_target")}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-cream-border pt-3">
          <MacroBar label={t("nutrition_day_detail.macro_protein")} current={d.protein_g} target={d.target_protein_g} />
          <MacroBar label={t("nutrition_day_detail.macro_carbs")} current={d.carbs_g} target={d.target_carbs_g} />
          <MacroBar label={t("nutrition_day_detail.macro_fat")} current={d.fat_g} target={d.target_fat_g} />
        </div>

        <div className="mt-3 grid grid-cols-3 gap-3 text-[12px] text-taupe">
          <div>
            Protein <span className="metric-number text-navy-deep">{d.adequacy.protein_per_kg}</span> {t("nutrition_archive.g_per_kg")}
          </div>
          <div>
            Carbs <span className="metric-number text-navy-deep">{d.adequacy.carbs_per_kg}</span> {t("nutrition_archive.g_per_kg")}
          </div>
          <div>
            Fat <span className="metric-number text-navy-deep">{d.adequacy.fat_pct_kcal}%</span> {t("nutrition_archive.kcal")}
          </div>
        </div>
      </Card>

      {d.adequacy.flags.length > 0 ? (
        <Card>
          <SectionLabel>{t("nutrition_day_detail.what_to_fix")}</SectionLabel>
          <div className="mt-3 space-y-2">
            {d.adequacy.flags.map((flag, i) => (
              <div
                key={i}
                className={`rounded-lg border p-3 ${SEV_STYLE[flag.severity] || SEV_STYLE.low}`}
              >
                <div className="text-[13px] font-medium text-navy-deep">{flag.issue}</div>
                {flag.suggestion ? (
                  <div className="mt-1 text-[12px] text-taupe">→ {flag.suggestion}</div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card>
        <SectionLabel>{t("nutrition_day_detail.meals_section")}</SectionLabel>
        {d.meals.length === 0 ? (
          <div className="mt-3 text-[13px] text-taupe">{t("nutrition_day_detail.no_meals_logged")}</div>
        ) : (
          <div className="mt-3 space-y-2">
            {d.meals.map((m) => (
              <Link
                key={m.id}
                to={`/nutrition/meal/${m.id}`}
                className="block rounded-lg border border-cream-border bg-cream-light p-3 hover:bg-cream"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[12px] text-taupe">
                      <span className="metric-number text-navy-deep">
                        {m.time || "—"}
                      </span>
                      <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-dark">
                        {MEAL_LABEL[m.meal_type] || m.meal_type}
                      </span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-[13px] text-navy-deep">
                      {m.description}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="metric-number text-[15px] text-navy-deep">
                      {m.calories}
                    </div>
                    <div className="text-[10px] text-taupe">{t("nutrition_archive.kcal")}</div>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-taupe">
                  {t("nutrition_day_detail.p_short")} {m.protein_g}{t("nutrition_page.g")} · {t("nutrition_day_detail.c_short")} {m.carbs_g}{t("nutrition_page.g")} · {t("nutrition_day_detail.f_short")} {m.fat_g}{t("nutrition_page.g")}
                  {m.fiber_g != null ? ` · ${t("nutrition_day_detail.fiber_short")} ${m.fiber_g}${t("nutrition_page.g")}` : ""}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function MacroBar({
  label,
  current,
  target,
}: {
  label: string;
  current: number;
  target: number;
}) {
  const { t } = useTranslation();
  const pct = target > 0 ? Math.min(150, Math.round((current / target) * 100)) : 0;
  const over = pct > 110;
  const under = pct < 80;
  const fillCls = over
    ? "bg-coral/60"
    : under
      ? "bg-amber/40"
      : "bg-amber/70";
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11px] text-taupe">
        <span>{label}</span>
        <span className="metric-number text-navy-deep">
          {Math.round(current)}/{target}{t("nutrition_page.g")}
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-cream-border">
        <div
          className={`h-full ${fillCls}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}
